import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [verified, setVerified] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setChecking(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setChecking(false);
      return;
    }

    try {
      const response = await api.get(`/subscription/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setVerified(true);
        setChecking(false);
        
        // REFRESH USER IMMEDIATELY - This updates AuthContext with Premium status
        console.log('✅ Payment confirmed - refreshing user data...');
        await refreshUser();
        console.log('✅ User data refreshed - Premium status active');
        
        return;
      }
      
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <Card className="max-w-md p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-orange-600 border-t-transparent mb-4"></div>
          <h2 className="text-2xl font-bold text-amber-900 mb-2">Verificando Pagamento...</h2>
          <p className="text-gray-600">Por favor, aguarde</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4">
      <Card className="max-w-md p-8 text-center border-2 border-green-200">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-amber-900 mb-4">🎉 Bem-vindo ao Premium!</h1>
        
        {verified ? (
          <>
            <p className="text-lg text-gray-700 mb-6">Seu pagamento foi confirmado com sucesso!</p>
          </>
        ) : (
          <>
            <p className="text-lg text-gray-700 mb-2">Pagamento processado!</p>
            <p className="text-gray-600 mb-6">Verifique seu email para confirmação</p>
          </>
        )}

        <div className="bg-orange-50 p-4 rounded-lg mb-6">
          <div className="flex items-center justify-center gap-2 text-orange-900 font-semibold mb-2">
            <Crown className="w-5 h-5" />
            Benefícios Desbloqueados:
          </div>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>✅ Receitas ilimitadas</li>
            <li>✅ IA ilimitada</li>
            <li>✅ Famílias ilimitadas</li>
            <li>✅ Sem anúncios</li>
          </ul>
        </div>

        <Button
          onClick={() => navigate('/')}
          className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white py-6"
        >
          Começar a Usar
        </Button>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;